<template>
  <app-framework>
    <header class="container">
      <bread-crumbs :list="[
        { name: $t('home'), link: '/' },
        { name: $t('general_settings_form') }
      ]" />
      <h1>{{ $t('general_settings_form') }}</h1>
    </header>
    <article class="container framework-slot">
      <div v-if="company.loading">
        <loading-state/>
      </div>
      <div v-else-if="company.error" class="alert alert-danger" role="alert">
        {{ $t('failed_retrieve_data_from_server') }}
        <button class="btn btn-link" @click="tryAgain">{{ $t('try_again') }}</button>
      </div>
      <form v-else v-on:submit.prevent="saveCompany">
        <div v-if="validationError" class="alert alert-danger" role="alert">
          {{ validationError }}
        </div>
        <form-page>
          <div class="form-row">
            <form-group class="col-12 col-md-6 required" :label="$t('display_name')" label-for="name" prepend-icon="tag"
                        :invalid-feedback="validationErrors.name" :description="$t('display_name_warning')">
              <input id="name" autocomplete="organization" type="text" class="form-control"
                     v-model="company.node.name" v-focus>
            </form-group>
            <form-group class="col-12 col-md-6" :label="$t('company_code')" label-for="code"
                        prepend-text="https://"
                        :invalid-feedback="validationErrors.code" append-text=".cardapio.app.br"
                        :description="$t('company_code_warning')">
              <input id="code" type="text" class="form-control" v-model="company.node.code" disabled>
            </form-group>
          </div>
          <div class="form-row">
            <form-group class="col-6 required" :label="$t('language_code')" label-for="language-code"
                        prepend-icon="language"
                        :invalid-feedback="validationErrors.languageCode">
              <select id="language-code" class="custom-select" v-model="company.node.languageCode">
                <option v-for="languageCode in languageCodes" :key="`language-code-${languageCode}`"
                        :value="languageCode">{{ $t(languageCode) }}
                </option>
              </select>
            </form-group>
            <form-group class="col-6 required" :label="$t('country_code')" label-for="country-code"
                        prepend-icon="globe-americas"
                        :invalid-feedback="validationErrors.countryCode">
              <select id="country-code" class="custom-select" v-model="company.node.countryCode">
                <option v-for="countryCode in countryCodes" :key="`country-code-${countryCode}`"
                        :value="countryCode">{{ $t(countryCode) }}
                </option>
              </select>
            </form-group>
            <form-group class="col-12 required" :label="$t('timezone')" label-for="timezone"
                        prepend-icon="clock" :description="$t('timezone_description')"
                        :invalid-feedback="validationErrors.timezone">
              <select id="country-code" class="custom-select" v-model="company.node.timezone">
                <option v-for="timezone in timezones" :key="`timezone-${timezone}`"
                        :value="timezone">{{ $t(timezone) }}
                </option>
              </select>
            </form-group>
          </div>
          <div class="form-row">
            <form-group class="col-12 col-md-6" :label="$t('facebook_link')" label-for="links-facebook"
                        :prepend-icon="['fab', 'facebook-f']" :invalid-feedback="validationErrors.linksFacebook">
              <input id="links-facebook" type="text" class="form-control" v-model="company.node.links.facebook">
            </form-group>
            <form-group class="col-12 col-md-6" :label="$t('instagram_link')" label-for="links-instagram"
                        :prepend-icon="['fab', 'instagram']" :invalid-feedback="validationErrors.linksInstagram">
              <input id="links-instagram" type="text" class="form-control" v-model="company.node.links.instagram">
            </form-group>
          </div>
          <div class="form-row">
            <form-group class="col-12 col-md-6" :label="$t('facebook_pixel_id')" :description="$t('facebook_pixel_id_description')" label-for="facebook-pixel-id"
                        prepend-icon="hashtag" :invalid-feedback="validationErrors.facebookPixelId">
              <input id="facebook-pixel-id" type="text" class="form-control" v-model="company.node.facebookPixelId">
            </form-group>
          </div>
          <div class="form-row">
            <form-group class="col-12" :label="$t('logo')" label-for="logo-url"
                        :invalid-feedback="validationErrors.logoUrl"
                        :description="!company.node.logoUrl ? $t('logo_description') : undefined">
              <input-image
                  ref="logoInput"
                  id="logo-url"
                  v-model="company.node.logoUrl"
                  :width="512"
                  :height="512"
                  :quality="0.9"
                  :thumbs="thumbSizes"
                  @thumbsReady="$event.forEach((thumbUrl, i) => company.node.logoThumbs[thumbSizes[i].ref] = thumbUrl)"
                  @reset="Object.getOwnPropertyNames(company.node.logoThumbs).forEach(key => delete company.node.logoThumbs[key])"
              />
            </form-group>
          </div>
          <div class="themes">
            <div class="form-group">
              <label class="form-label">{{ $t('themes') }}</label>
              <div class="input-group">
                <div class="row">
                  <div class="col-6 col-md-4 col-lg-3" v-for="themeName in themeNames" :key="`theme-${themeName}`">
                    <div class="theme">
                      <label class="theme-image-container">
                        <input :id="`theme-${themeName}`" type="radio" name="themeName" :value="themeName" v-model="company.node.themeName" tabindex="-1">
                        <img :src="`https://storage.googleapis.com/foodsoft-br/public-gallery/themes/${themeName}.jpg`" :alt="themeName">
                      </label>
                      <button type="button" :for="`theme-${themeName}`" class="btn btn-sm btn-outline-secondary" tabindex="0" @click="company.node.themeName = themeName">{{ $t('theme_choose') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form-page>
        <footer>
          <div class="form-row">
            <div class="col-auto">
              <button :disabled="company.saving" class="btn btn-primary"
                      v-text="!company.saving ? $t('save_button') : $t('saving')"></button>
            </div>
          </div>
        </footer>
      </form>
    </article>
  </app-framework>
</template>

<style scoped lang="scss">
  .theme {
    position: relative;
    margin-bottom: 15px;

    .theme-image-container {
      margin-bottom: 0;
      background: #fff;
    }

    input {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
    }

    img {
      width: 100%;
      border-radius: 4px;
      border: 1px solid #ced4da;
      padding: 5px;
      cursor: pointer;
    }

    .btn {
      margin-top: 5px;
      cursor: pointer;

      &:hover ~ img {
        border: 1px solid #262c40;
      }
    }

    .theme-image-container:hover img {
      border: 1px solid #262c40;
    }

    .theme-image-container input:focus + img, .theme-image-container input:active + img {
      box-shadow: 0 0 0 0.2rem rgba(38, 44, 64, 0.50);
      border: 1px solid #262c40;
    }

    .theme-image-container input:checked + img {
      border: 2px solid #d26c4f;
      padding: 4px;
    }

    .theme-image-container input:checked:focus + img, .theme-image-container input:checked:active + img {
      box-shadow: 0 0 0 0.2rem rgba(210, 108, 79, 0.5);
    }

  }
</style>

<script>
import FormMixin from "../mixins/FormMixin";
import AppFramework from "../components/layout/AppFramework";
import FormPage from "../components/common/FormPage";
import FormGroup from "../components/common/FormGroup";
import LoadingState from "../components/states/LoadingState";
import gql from "graphql-tag";
import InputImage from "@/components/common/InputImage";
import BreadCrumbs from "@/components/layout/BreadCrumbs";

export default {
  name: 'generalSettingsForm',
  i18nOptions: {namespaces: ['general_settings', 'common', 'breadcrumbs', 'error']},
  components: {BreadCrumbs, InputImage, LoadingState, FormGroup, FormPage, AppFramework},
  mixins: [FormMixin],
  metaInfo() {
    return {
      title: this.$t('general_settings_form')
    }
  },
  data: () => ({
    company: {
      node: null,
      loading: true,
      error: null,
      saving: false
    },
    thumbSizes: [
      {ref: "113x113", width: 113, height: 113},
      {ref: "226x226", width: 226, height: 226}
    ],
    kindsOfPerson: [
      "NATURAL_PERSON",
      "LEGAL_PERSON"
    ],
    countryCodes: [
      "BR",
      // "US"
    ],
    languageCodes: [
      "pt-BR",
      // "en-US"
    ],
    timezones: [
      "America/Noronha",
      "America/Belem",
      "America/Fortaleza",
      "America/Recife",
      "America/Araguaina",
      "America/Maceio",
      "America/Bahia",
      "America/Sao_Paulo",
      "America/Campo_Grande",
      "America/Cuiaba",
      "America/Santarem",
      "America/Porto_Velho",
      "America/Boa_Vista",
      "America/Manaus",
      "America/Eirunepe",
      "America/Rio_Branco"
    ],
    themeNames: [
      "red",
      "yellow",
      "green",
      "black",
      "purple",
      "orange",
      "blue",
      "pink"
    ]
  }),
  computed: {
    isNew: function () {
      return this.$route.params.id === "new";
    },
  },
  async mounted() {
    await this.queryCompany();
  },
  methods: {
    async tryAgain() {
      if (!this.isNew && this.company.error) {
        await this.queryCompany();
      }
    },
    async queryCompany() {
      this.company.loading = true;
      try {
        const response = await this.$apolloClient.query({
          query: gql`query {
            myCompany {
              id
              name
              code
              kindOfPerson
              legalName
              regNumber
              logoUrl
              logoThumbs
              themeName
              languageCode
              countryCode
              timezone
              links
              facebookPixelId
            }
          }`,
          fetchPolicy: "network-only",
        });
        this.company.node = response.data.myCompany;
        // if (!this.company.node.kindOfPerson) {
        //   this.$set(this.company.node, 'kindOfPerson', this.kindsOfPerson[0])
        // }
        // if (!this.company.node.languageCode) {
        //   this.$set(this.company.node, 'languageCode', this.languageCodes[0])
        // }
        if (!this.company.node.links) {
          this.$set(this.company.node, 'links', {})
        }
        if (!this.company.node.logoThumbs) {
          this.$set(this.company.node, 'logoThumbs', {})
        }

        this.company.error = null;
      } catch (e) {
        this.company.error = e.message
      }
      this.company.loading = false;
    },
    async saveCompany() {
      if (this.company.saving || !this.checkForm()) {
        return;
      }
      this.disableForm();
      this.company.saving = true;
      try {
        await this.$refs.logoInput.waitForUpload()
        await this.$apolloClient.mutate({
          mutation: gql`mutation ($input: CompanyInput!) {
            updateMyCompany (
              input: $input
            ) {
              id
              name
              code
              kindOfPerson
              legalName
              regNumber
              logoUrl
              logoThumbs
              themeName
              languageCode
              countryCode
              timezone
              links
              facebookPixelId
            }
          }`,
          variables: {
            input: {
              name: this.company.node.name,
              code: this.company.node.code,
              kindOfPerson: this.company.node.kindOfPerson || null,
              legalName: this.company.node.legalName || null,
              regNumber: this.company.node.regNumber || null,
              logoUrl: this.company.node.logoUrl || null,
              logoThumbs: this.company.node.logoThumbs || null,
              themeName: this.company.node.themeName || null,
              languageCode: this.company.node.languageCode,
              countryCode: this.company.node.countryCode,
              timezone: this.company.node.timezone,
              links: this.company.node.links || null,
              facebookPixelId: this.company.node.facebookPixelId || null
            },
          }
        });
        await this.$apolloClient.resetStore();
        // this.company.saving = false;
        // this.activeForm();
        await this.$router.push('/');
      } catch (e) {
        this.company.saving = false;
        this.activeForm();
        this.showError(e);
      }
    },
    validateForm() {
      this.validateFormMixin();
    }
  }
}
</script>
